import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

/**
 * Filter state for S3 file browser
 */
export interface S3FilterState {
  selectedYear: string | null;
  selectedMonths: string[];
  selectedPartners: string[];
  searchText: string;
}

/**
 * Context value type
 */
interface S3FilterContextValue {
  filters: S3FilterState;
  setSelectedYear: (year: string | null) => void;
  setSelectedMonths: (months: string[]) => void;
  setSelectedPartners: (partners: string[]) => void;
  setSearchText: (text: string) => void;
  applyFilters: () => void;
  clearFilters: () => void;
  hasChanges: boolean;
}

/**
 * Storage key for filter persistence
 */
const FILTER_STORAGE_KEY = 's3_browser.filters';

/**
 * Default filter state
 */
const DEFAULT_FILTERS: S3FilterState = {
  selectedYear: null,
  selectedMonths: [],
  selectedPartners: [],
  searchText: '',
};

/**
 * Create context
 */
const S3FilterContext = createContext<S3FilterContextValue | null>(null);

/**
 * Get initial filter state from localStorage
 */
const getInitialFilters = (): S3FilterState => {
  if (typeof window === 'undefined') {
    return DEFAULT_FILTERS;
  }

  try {
    const stored = window.localStorage.getItem(FILTER_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.warn('Failed to parse stored filters:', error);
  }

  return DEFAULT_FILTERS;
};

/**
 * S3Filter provider component
 */
export const S3FilterProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [filters, setFilters] = useState<S3FilterState>(() => getInitialFilters());
  const [pendingFilters, setPendingFilters] = useState<S3FilterState>(filters);

  /**
   * Persist filters to localStorage whenever they change
   */
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      window.localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
    } catch (error) {
      console.warn('Failed to persist filters:', error);
    }
  }, [filters]);

  /**
   * Set selected year in pending filters
   */
  const setSelectedYear = (year: string | null) => {
    setPendingFilters((prev) => ({
      ...prev,
      selectedYear: year,
    }));
  };

  /**
   * Set selected months in pending filters
   */
  const setSelectedMonths = (months: string[]) => {
    setPendingFilters((prev) => ({
      ...prev,
      selectedMonths: months,
    }));
  };

  /**
   * Set selected partners in pending filters
   */
  const setSelectedPartners = (partners: string[]) => {
    setPendingFilters((prev) => ({
      ...prev,
      selectedPartners: partners,
    }));
  };

  /**
   * Set search text in pending filters
   */
  const setSearchText = (text: string) => {
    setPendingFilters((prev) => ({
      ...prev,
      searchText: text,
    }));
  };

  /**
   * Apply pending filters to active filters
   */
  const applyFilters = () => {
    setFilters(pendingFilters);
  };

  /**
   * Clear all filters and reset to defaults
   */
  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setPendingFilters(DEFAULT_FILTERS);
  };

  /**
   * Check if pending filters differ from applied filters
   */
  const hasChanges = useMemo(() => {
    return (
      pendingFilters.selectedYear !== filters.selectedYear ||
      JSON.stringify(pendingFilters.selectedMonths) !== JSON.stringify(filters.selectedMonths) ||
      JSON.stringify(pendingFilters.selectedPartners) !== JSON.stringify(filters.selectedPartners) ||
      pendingFilters.searchText !== filters.searchText
    );
  }, [pendingFilters, filters]);

  /**
   * Context value
   */
  const value = useMemo<S3FilterContextValue>(
    () => ({
      filters: pendingFilters,
      setSelectedYear,
      setSelectedMonths,
      setSelectedPartners,
      setSearchText,
      applyFilters,
      clearFilters,
      hasChanges,
    }),
    [pendingFilters, hasChanges]
  );

  return <S3FilterContext.Provider value={value}>{children}</S3FilterContext.Provider>;
};

/**
 * Hook to use S3Filter context
 */
export const useS3Filter = (): S3FilterContextValue => {
  const context = useContext(S3FilterContext);
  if (!context) {
    throw new Error('useS3Filter must be used within an S3FilterProvider');
  }
  return context;
};
